import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNetwork } from '../contexts/NetworkContext';
import { useWallet } from '../contexts/WalletContext';
import { useContract } from '../contexts/ContractContext';
import { 
  getERC3643Info, 
  checkIsAgent, 
  performTransfer, 
  performTransferFrom,
  performMint, 
  performBurn,
  setAddressFrozen,
  freezePartialTokens,
  unfreezePartialTokens,
  pauseToken,
  unpauseToken,
  formatAmount,
  parseAmount,
  getERC3643SignerContract,
  ERC3643Info
} from '../contracts/contract';
import Header from './Header';
import PolicyDescription from './PolicyDescription';
import UserOperations from './UserOperations';
import AgentOperations from './AgentOperations';
import ContractAddressInput from './ContractAddressInput';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { currentNetwork } = useNetwork();
  const { provider, signer, account, isConnected, onAccountChange } = useWallet();
  const { isAddressSet, contractAddress } = useContract();
  
  // Get network from currentNetwork
  const displayNetwork = useMemo(() => {
    return currentNetwork;
  }, [currentNetwork]);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [tokenInfo, setTokenInfo] = useState<ERC3643Info | null>(null);
  const [isAgent, setIsAgent] = useState(false);
  
  
  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);
  
  // Load token information
  const loadTokenInfo = useCallback(async () => {
    if (!provider || !contractAddress || !account) return;
    
    try {
      setLoading(true);
      const [tokenData, agentStatus] = await Promise.all([
        getERC3643Info(provider, contractAddress, account),
        checkIsAgent(provider, contractAddress, account)
      ]);
      
      setTokenInfo(tokenData);
      setIsAgent(agentStatus);
    } catch (err) {
      setError('Failed to load token information: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [provider, contractAddress, account]);

  // Handle transfer
  const handleTransfer = async (to: string, amount: string) => {
    if (!signer || !contractAddress || !account || !displayNetwork) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await performTransfer(
        signer, 
        contractAddress, 
        to, 
        parsedAmount,
        displayNetwork.chainId,
        account
      );
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Transfer failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle transfer from
  const handleTransferFrom = async (from: string, to: string, amount: string) => {
    if (!signer || !contractAddress || !account || !displayNetwork) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await performTransferFrom(
        signer, 
        contractAddress, 
        from,
        to, 
        parsedAmount,
        displayNetwork.chainId,
        account
      );
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Transfer From failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async (spender: string, amount: string) => {
    if (!signer || !contractAddress) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      const contract = getERC3643SignerContract(signer, contractAddress);
      await contract.approve(spender, parsedAmount);
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Approve failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle mint
  const handleMint = async (to: string, amount: string) => {
    if (!signer || !contractAddress || !account || !displayNetwork) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await performMint(
        signer, 
        contractAddress, 
        to, 
        parsedAmount,
        displayNetwork.chainId,
        account
      );
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Mint failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle burn
  const handleBurn = async (address: string, amount: string) => {
    if (!signer || !contractAddress || !account || !displayNetwork) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await performBurn(
        signer, 
        contractAddress, 
        address, 
        parsedAmount,
        displayNetwork.chainId,
        account
      );
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Burn failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle freeze address
  const handleFreezeAddress = async (address: string, freeze: boolean) => {
    if (!signer || !contractAddress) return;
    
    try {
      setLoading(true);
      await setAddressFrozen(signer, contractAddress, address, freeze);
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError(`Freeze operation failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle freeze partial tokens
  const handleFreezePartial = async (address: string, amount: string) => {
    if (!signer || !contractAddress) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await freezePartialTokens(signer, contractAddress, address, parsedAmount);
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Freeze partial failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle unfreeze partial tokens
  const handleUnfreezePartial = async (address: string, amount: string) => {
    if (!signer || !contractAddress) return;
    
    try {
      setLoading(true);
      const parsedAmount = parseAmount(amount, tokenInfo?.decimals || 18);
      await unfreezePartialTokens(signer, contractAddress, address, parsedAmount);
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Unfreeze partial failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Handle pause/unpause
  const handlePauseToggle = async () => {
    if (!signer || !contractAddress) return;
    
    try {
      setLoading(true);
      if (tokenInfo?.isPaused) {
        await unpauseToken(signer, contractAddress);
      } else {
        await pauseToken(signer, contractAddress);
      }
      await loadTokenInfo(); // Refresh token info
    } catch (err) {
      setError('Pause toggle failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Load token info when contract is connected
  useEffect(() => {
    if (isConnected && provider && displayNetwork && contractAddress && account) {
      loadTokenInfo();
    }
  }, [isConnected, provider, displayNetwork, contractAddress, account, loadTokenInfo]);

  // Listen for account changes
  useEffect(() => {
    const unsubscribe = onAccountChange((newAccount) => {
      // Reload token info when account changes
      if (provider && newAccount && displayNetwork && contractAddress) {
        loadTokenInfo();
      }
    });
    
    return unsubscribe;
  }, [onAccountChange, provider, displayNetwork, contractAddress, loadTokenInfo]);

  // Update header height on component mount and when header changes
  useEffect(() => {
    // Initial update with a small delay to ensure header is rendered
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);
    
    // Set up ResizeObserver to watch for header height changes
    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      resizeObserver.observe(header);
      
      // Also listen for window resize events
      const handleResize = () => {
        updateHeaderHeight();
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  return (
    <div className="home-page">
      <Header subtitle="ERC3643" />
      
      <main className="container">
        <div className="main-content">
            
            <PolicyDescription 
              currentNetwork={displayNetwork || undefined}
              contractAddress={contractAddress}
              isConnected={isConnected}
              provider={provider}
            />

            {!isConnected ? (
              <div className="connect-section">
                <h2>Connect Your Wallet</h2>
                <p>Please connect your wallet to interact with TrustLine-protected smart contracts.</p>
          </div>
            ) : !displayNetwork ? (
              <div className="no-network">
                <h3>No Network Selected</h3>
                <p>Please select a supported network to continue.</p>
        </div>
            ) : !isAddressSet ? (
              <ContractAddressInput />
            ) : (
              <div className="token-interface">
                {/* Token Information */}
                {error && <div className="error">{error}</div>}
                {tokenInfo && (
                  <div className="token-info-card">
                    <h3>{tokenInfo.name} ({tokenInfo.symbol})</h3>
                    <div className="token-details">
                      <p><strong>Your Balance:</strong> {formatAmount(tokenInfo.balance, tokenInfo.decimals)} {tokenInfo.symbol}</p>
                      <p><strong>Total Supply:</strong> {formatAmount(tokenInfo.totalSupply, tokenInfo.decimals)} {tokenInfo.symbol}</p>
                      <p><strong>Status:</strong> <span className={`status ${tokenInfo.isPaused ? 'paused' : 'active'}`}>
                        {tokenInfo.isPaused ? 'Paused' : 'Active'}
                      </span></p>
                      <p><strong>Role:</strong> <span className={`status ${isAgent ? 'agent' : 'user'}`}>
                        {isAgent ? 'Agent' : 'User'}
                      </span></p>
                    </div>
                  </div>
                )}

                {/* User Operations */}
                <UserOperations
                  tokenInfo={tokenInfo}
                  onTransfer={handleTransfer}
                  onTransferFrom={handleTransferFrom}
                  onApprove={handleApprove}
                  loading={loading}
                  provider={provider}
                  account={account}
                  contractAddress={contractAddress}
                />

                {/* Agent Operations */}
                {isAgent && (
                  <AgentOperations
                    tokenInfo={tokenInfo}
                    onMint={handleMint}
                    onBurn={handleBurn}
                    onFreezeAddress={handleFreezeAddress}
                    onFreezePartial={handleFreezePartial}
                    onUnfreezePartial={handleUnfreezePartial}
                    onPauseToggle={handlePauseToggle}
                    loading={loading}
                  />
                )}
              </div>
            )}
            
            <div className="loading" style={{ visibility: loading ? 'visible' : 'hidden' }}>
              Loading...
          </div>
        </div>
      </main>
      
      <footer>
        <p>Contact us at <a href="mailto:contact@trustline.id">contact@trustline.id</a></p>
      </footer>
    </div>
  );
};

export default HomePage;
